import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { TooltipProvider } from '@/components/ui/tooltip';
import App from './App';
import { logBootNavigation } from './lib/version-monitor';
import { initVisualViewportSync } from './utils/visual-viewport';
import SidebarPreviewPage from './pages/dev/SidebarPreviewPage';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Dev-only handle for visual validation / harness scripts to invalidate
// individual query keys. Stripped from production by the import.meta.env.DEV
// guard. Useful when an agent-browser session mocks a fetch endpoint and
// needs to discard cached data without a full page reload.
if (import.meta.env.DEV) {
  (window as unknown as { __queryClient?: QueryClient }).__queryClient =
    queryClient;
}

// Reload forensics: record how this boot happened (navigate / reload /
// programmatic reload breadcrumb) before anything else can navigate.
logBootNavigation();

// Keep full-height shells above the on-screen keyboard (Tesla / tablets).
initVisualViewportSync();

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Root element not found');

// DEV-only standalone preview that renders inside its own MemoryRouter
// (cannot nest a Router inside BrowserRouter). Bypasses App entirely.
const isDevSidebarPreview =
  import.meta.env.DEV && window.location.pathname === '/dev/sidebar-preview';

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={200}>
        {isDevSidebarPreview ? (
          <SidebarPreviewPage />
        ) : (
          <BrowserRouter>
            <App />
          </BrowserRouter>
        )}
      </TooltipProvider>
    </QueryClientProvider>
  </StrictMode>
);
